/**
 * JobStatusTable Component
 *
 * Generic table component for displaying job status across all job types
 * (indexing, offer sync, history). Color-coded status badges and progress indicators.
 */

import React from 'react';
import type {
  CollectionIndexingJobSummary,
  OfferSyncJobSummary,
  HistoryJobSummary,
  UnifiedJobSummary,
} from '../../types/admin';

type JobSummary = CollectionIndexingJobSummary | OfferSyncJobSummary | HistoryJobSummary | UnifiedJobSummary;

interface JobStatusTableProps {
  jobs: JobSummary[];
  jobType: 'indexing' | 'offerSync' | 'history' | 'unified';
  emptyMessage?: string;
}

export function JobStatusTable({ jobs, jobType, emptyMessage = 'No jobs found' }: JobStatusTableProps) {
  const [copiedText, setCopiedText] = React.useState<string | null>(null);

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedText(label);
      setTimeout(() => setCopiedText(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'queued':
        return 'bg-blue-100 text-blue-800';
      case 'processing':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      case 'rate_limited':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = (startedAt: string | null, completedAt: string | null) => {
    if (!startedAt || !completedAt) return '-';
    const duration = new Date(completedAt).getTime() - new Date(startedAt).getTime();
    const seconds = Math.floor(duration / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${minutes % 60}m`;
  };

  const calculateEstimatedTimeRemaining = (job: HistoryJobSummary): string => {
    // Only calculate for processing jobs
    if (job.status !== 'processing' || !job.processingStartedAt) {
      return '-';
    }

    const processed = job.nftsProcessed || 0;
    const total = job.totalNfts || 0;
    const remaining = total - processed;

    if (remaining <= 0 || processed === 0) {
      return '-';
    }

    // Calculate time elapsed
    const startTime = new Date(job.processingStartedAt).getTime();
    const now = new Date().getTime();
    const elapsedMs = now - startTime;
    const elapsedSeconds = elapsedMs / 1000;

    // Calculate processing rate (items per second)
    const rate = processed / elapsedSeconds;

    if (rate <= 0) {
      return 'Calculating...';
    }

    // For history jobs with rate limiting (1 req/sec), add rate limit factor
    // Bithomp API is limited to 1 request per second
    const rateLimit = 1; // 1 request per second
    const effectiveRate = Math.min(rate, rateLimit);

    // Estimate remaining time in seconds
    const estimatedSeconds = remaining / effectiveRate;

    // Format the time
    if (estimatedSeconds < 60) {
      return `~${Math.ceil(estimatedSeconds)}s`;
    }
    const minutes = Math.floor(estimatedSeconds / 60);
    if (minutes < 60) {
      return `~${minutes}m`;
    }
    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      return `~${hours}h ${minutes % 60}m`;
    }
    const days = Math.floor(hours / 24);
    return `~${days}d ${hours % 24}h`;
  };

  if (jobs.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        {emptyMessage}
      </div>
    );
  }

  // For unified job type, calculate progress dynamically
  const getProgress = (job: JobSummary): number => {
    if ('progress' in job) {
      return job.progress;
    }
    // For UnifiedJobSummary, calculate progress from totalItems and itemsProcessed
    const unifiedJob = job as UnifiedJobSummary;
    if (unifiedJob.totalItems && unifiedJob.itemsProcessed) {
      return Math.round((unifiedJob.itemsProcessed / unifiedJob.totalItems) * 100);
    }
    return 0;
  };

  return (
    <div className="overflow-x-auto -mx-4 sm:mx-0">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {jobType === 'unified' && (
              <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
            )}
            {jobType === 'unified' && (
              <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                Collection
              </th>
            )}
            {jobType === 'unified' && (
              <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                Collection ID
              </th>
            )}
            <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
              Job ID
            </th>
            <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Progress
            </th>
            {(jobType === 'indexing' || jobType === 'history') && (
              <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                Items
              </th>
            )}
            {jobType === 'history' && (
              <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                ETA
              </th>
            )}
            <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
              Queued
            </th>
            <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
              Duration
            </th>
            {jobType === 'offerSync' && (
              <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                Triggered By
              </th>
            )}
            <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
              Error
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {jobs.map((job) => {
            const progress = getProgress(job);
            return (
              <tr key={job.id} className="hover:bg-gray-50">
                {jobType === 'unified' && (
                  <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs sm:text-sm text-gray-700">
                    <span className="font-medium">
                      {(job as UnifiedJobSummary).jobType}
                    </span>
                  </td>
                )}
                {jobType === 'unified' && (
                  <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs sm:text-sm text-gray-700 hidden sm:table-cell">
                    {(job as UnifiedJobSummary).collectionName || `${(job as UnifiedJobSummary).issuerAddress.substring(0, 8)}...`}
                  </td>
                )}
                {jobType === 'unified' && (
                  <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs sm:text-sm font-mono text-gray-700 hidden lg:table-cell">
                    {(job as UnifiedJobSummary).collectionId ? (
                      <div className="flex items-center gap-2">
                        <span>{(job as UnifiedJobSummary).collectionId!.substring(0, 8)}...</span>
                        <button
                          onClick={() => copyToClipboard((job as UnifiedJobSummary).collectionId!, `collection-${job.id}`)}
                          className="text-gray-400 hover:text-gray-600 transition-colors"
                          title="Copy collection ID"
                        >
                          {copiedText === `collection-${job.id}` ? (
                            <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          ) : (
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          )}
                        </button>
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                )}
                <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs sm:text-sm font-mono text-gray-900 hidden md:table-cell">
                  <div className="flex items-center gap-2">
                    <span>{job.id.substring(0, 8)}...</span>
                    <button
                      onClick={() => copyToClipboard(job.id, `job-${job.id}`)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                      title="Copy job ID"
                    >
                      {copiedText === `job-${job.id}` ? (
                        <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </td>
                <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(job.status)}`}>
                    {job.status}
                  </span>
                </td>
                <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap">
                  <div className="flex items-center gap-1 sm:gap-2">
                    <div className="w-16 sm:w-24 bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${
                          job.status === 'completed' ? 'bg-green-600' :
                          job.status === 'failed' ? 'bg-red-600' :
                          'bg-blue-600'
                        }`}
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <span className="text-xs sm:text-sm text-gray-600">{progress}%</span>
                  </div>
                </td>
                {(jobType === 'indexing' || jobType === 'history') && (
                  <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs sm:text-sm text-gray-700 hidden sm:table-cell">
                    {jobType === 'indexing' && 'nftsProcessed' in job && (
                      <>
                        {(job as CollectionIndexingJobSummary).nftsProcessed}/{(job as CollectionIndexingJobSummary).totalNfts}
                        {(job as CollectionIndexingJobSummary).nftsFailed > 0 && (
                          <span className="ml-1 text-red-600">({(job as CollectionIndexingJobSummary).nftsFailed})</span>
                        )}
                      </>
                    )}
                    {jobType === 'history' && 'nftsProcessed' in job && (
                      <>
                        {(job as HistoryJobSummary).nftsProcessed}/{(job as HistoryJobSummary).totalNfts}
                        {(job as HistoryJobSummary).nftsFailed > 0 && (
                          <span className="ml-1 text-red-600">({(job as HistoryJobSummary).nftsFailed})</span>
                        )}
                      </>
                    )}
                  </td>
                )}
                {jobType === 'history' && (
                  <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs sm:text-sm text-gray-700 hidden md:table-cell">
                    {calculateEstimatedTimeRemaining(job as HistoryJobSummary)}
                  </td>
                )}
                <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs sm:text-sm text-gray-500 hidden lg:table-cell">
                  {formatDate(job.queuedAt)}
                </td>
                <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs sm:text-sm text-gray-500 hidden sm:table-cell">
                  {formatDuration(job.processingStartedAt, job.processingCompletedAt)}
                </td>
                {jobType === 'offerSync' && (
                  <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap text-xs sm:text-sm text-gray-500 hidden lg:table-cell">
                    {(job as OfferSyncJobSummary).triggeredBy}
                  </td>
                )}
                <td className="px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-500 max-w-xs truncate hidden md:table-cell">
                  {job.errorMessage || '-'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
